import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { TONE_CONFIG } from "@/lib/format";
import { Bell, Clock, FileText } from "lucide-react";

const TPL_STATUS = {
  APPROVED: "bg-emerald-50 text-emerald-700", PENDING_APPROVAL: "bg-amber-50 text-amber-700",
  DRAFT: "bg-slate-100 text-slate-600", REJECTED: "bg-red-50 text-red-700", PAUSED: "bg-slate-100 text-slate-500",
};

export default function Recordatorios() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [org, setOrg] = useState(null);
  const canEdit = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  useEffect(() => {
    api.get("/settings/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/settings/organization").then((r) => setOrg(r.data)).catch(() => {});
  }, []);

  const updateRule = (idx, field, value) => {
    const rules = [...org.escalation_rules];
    rules[idx] = { ...rules[idx], [field]: field === "day" ? parseInt(value || 0) : value };
    setOrg({ ...org, escalation_rules: rules });
  };

  const save = async () => {
    await api.put("/settings/organization", { escalation_rules: org.escalation_rules });
    toast.success("Reglas de escalamiento guardadas");
  };

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-3xl font-bold text-slate-900">Recordatorios</h1><p className="text-slate-500 mt-1">Plantillas persuasivas y escalamiento automático.</p></div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2"><FileText size={18} className="text-blue-600" /> Plantillas de mensaje</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <div key={t.id} className="p-4 rounded-xl border border-slate-100 bg-slate-50">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-sm text-slate-700">{t.name}</span>
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${TPL_STATUS[t.status]}`}>{t.status}</span>
              </div>
              <p className="text-xs text-slate-500">Tono: {TONE_CONFIG[t.tone]?.label || t.tone} · Canal: {t.channel} · {t.language}</p>
              <div className="flex flex-wrap gap-1 mt-2">{t.variables.map((v) => <span key={v} className="text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-500">{`{${v}}`}</span>)}</div>
            </div>
          ))}
        </div>
      </div>

      {org && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2"><Clock size={18} className="text-teal-600" /> Escalamiento automático</h2>
          <p className="text-xs text-slate-500 mb-4">El tono aumenta según los días sin actividad. La plataforma sigue recordando aunque el trabajador no responda.</p>
          <div className="space-y-2">
            {org.escalation_rules.map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm text-slate-600 w-24"><Bell size={14} /> Día
                  <input type="number" disabled={!canEdit} value={r.day} onChange={(e) => updateRule(i, "day", e.target.value)} className="w-14 ml-1 px-2 py-1 rounded border border-slate-200 text-sm" />
                </div>
                <select disabled={!canEdit} value={r.tone} onChange={(e) => updateRule(i, "tone", e.target.value)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white flex-1 max-w-xs">
                  {["SOFT", "STANDARD", "URGENT", "FINAL_REMINDER", "INTERNAL_ALERT"].map((t) => <option key={t} value={t}>{TONE_CONFIG[t]?.label || "Alerta interna RR.HH."}</option>)}
                </select>
              </div>
            ))}
          </div>
          {canEdit && <button data-testid="save-escalation" onClick={save} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">Guardar reglas</button>}
        </div>
      )}
    </div>
  );
}
